"use client";

import React, { useRef, useState } from "react";
import MinusIcon from "@/assets/icons/minus-white.svg";
import Pause from "@/assets/icons/pause.svg";
import Play from "@/assets/icons/play-black.svg";
import PlusIcon from "@/assets/icons/plus-white.svg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import type { MultiDietGuideType } from "@/features/static-builder/types/multidiet-guide.type";
import { interpretColors } from "@/lib/helpers/typography";
import { cn } from "@/lib/utils";

export const MultiDietGuideMobile = ({
  data,
  videoUrls,
}: {
  data: MultiDietGuideType;
  videoUrls: string[];
}) => {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [isPlaying, setIsPlaying] = useState(true);

  const mergedData = data.guideSteps.map((step, idx) => ({
    ...step,
    video: videoUrls[idx],
    description: data.descriptions[idx],
  }));

  const togglePlay = (index: number) => {
    const video = videoRefs.current[index];

    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }

    setIsPlaying(!isPlaying);
  };

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="0"
      className="flex w-full flex-col gap-5 lg:hidden"
    >
      {mergedData.map((item, index) => (
        <AccordionItem
          key={item.id}
          value={index.toString()}
          className="rounded-lg border-none bg-brand-grey4"
        >
          <AccordionTrigger
            className="rounded-xl bg-brand-green2-300 px-6 text-left font-medium text-lg [&[data-state=closed]_.minus]:hidden [&[data-state=open]>svg]:rotate-0 [&[data-state=open]_.plus]:hidden [&_.lucide-chevron-down]:hidden"
            showArrow={false}
          >
            <div className="flex items-center gap-2 rounded-3xl bg-brand-grey1 px-2 py-[0.4em]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-green2-300 p-3 text-brand-white">
                {index + 1}
              </span>
              <p className="font-medium text-brand-green2-300">{item.label}</p>
            </div>
            <MinusIcon className="minus" />
            <PlusIcon className="plus" />
          </AccordionTrigger>
          <AccordionContent className="min-h-[800px] p-6">
            <h3 className="title font-medium text-2xl text-brand-green1-300 leading-8">
              {item.title}
            </h3>
            <div className="mt-6">
              {item.description?.text.map((paragraph, paraIdx) => {
                return (
                  <p
                    key={paraIdx}
                    className={cn(`text-brand-green-1 text-lg last:pt-3`)}
                  >
                    {paragraph.children.map((child, childIdx) => (
                      <span
                        key={childIdx}
                        className={cn(child.bold && "font-bold")}
                        dangerouslySetInnerHTML={{
                          __html: interpretColors(child.text),
                        }}
                      />
                    ))}
                  </p>
                );
              })}
            </div>
            <div className="relative mt-6 h-[530px] w-full overflow-hidden rounded-2xl border-8 border-brand-grey1 md:h-[600px] md:w-[60%]">
              <video
                ref={(el: HTMLVideoElement | null) => {
                  videoRefs.current[index] = el;
                }}
                controls={false}
                autoPlay
                loop
                playsInline
                className="z-0 aspect-video h-full w-full rounded-2xl object-cover object-top"
                src={`${item.video}`}
              />
              <Button
                onClick={() => togglePlay(index)}
                type="button"
                variant="ghost"
                className="absolute bottom-0 left-0 h-12 w-12 rounded-xl border-1 border-brand-green1-300 bg-brand-white p-2"
                size="lg"
              >
                {isPlaying ? (
                  <Pause className="h-6 w-6" />
                ) : (
                  <Play className="h-6 w-6" />
                )}
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
